import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Lightbulb } from "lucide-react";
import ChatLeadForm from "@/components/ChatLeadForm";
import { whatsappLink } from "@/lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/chat`;
const WELCOME = "Ciao! Sono l'assistente di Idea 360. Posso spiegarti come lavoriamo, quali servizi offriamo o aiutarti a capire da dove partire. Cosa ti serve?";

const getSession = () => {
  let id = localStorage.getItem("idea360_chat_session");
  if (!id) {
    id = `s-${crypto.randomUUID()}`;
    localStorage.setItem("idea360_chat_session", id);
  }
  return id;
};

const Bubble = ({ role, text }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${role}`}>
    <div
      className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
        role === "user" ? "bg-[#FACC15] text-black" : "bg-[#161616] text-white/90 border border-[#262626]"
      }`}
    >
      {text || <span className="inline-block w-2 h-4 bg-[#FACC15] animate-pulse" />}
    </div>
  </div>
);

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [showForm, setShowForm] = useState(false);
  const endRef = useRef(null);
  const sessionId = useRef(getSession());

  useEffect(() => {
    fetch(`${API}/history/${sessionId.current}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((h) => h.length && setMessages([{ role: "assistant", content: WELCOME }, ...h]))
      .catch(() => {});
    fetch(`${API}/lead/${sessionId.current}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.captured) {
          setLeadDone(true);
          setLeadName(d.name || "");
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    const append = (delta) =>
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + delta };
        return copy;
      });

    try {
      const res = await fetch(`${API}/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("http");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.type === "delta") append(ev.text);
          if (ev.type === "error") append(ev.message);
          if (ev.type === "done" && ev.show_form && !leadDone) setShowForm(true);
        }
      }
    } catch {
      append("Si è verificato un problema di connessione. Puoi scriverci dalla pagina Contatti.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Chiudi assistente" : "Apri assistente"}
        data-testid="chat-toggle"
        className="fixed bottom-[86px] right-4 md:bottom-6 md:right-6 z-[60] w-14 h-14 bg-[#FACC15] text-black ring-2 ring-black/80 flex items-center justify-center shadow-[0_0_24px_rgba(250,204,21,0.45)] hover:bg-[#EAB308] transition-colors"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div
          className="fixed bottom-[150px] inset-x-3 md:bottom-24 md:inset-x-auto md:right-6 z-[60] w-auto md:w-[380px] h-[min(560px,calc(100vh-15rem))] md:h-[min(560px,calc(100vh-8.5rem))] bg-[#0a0a0a] border border-[#262626] flex flex-col shadow-2xl"
          data-testid="chat-panel"
        >
          <div className="px-5 py-4 border-b border-[#1f1f1f] flex items-center gap-3">
            <Lightbulb size={20} className="text-[#FACC15]" />
            <div>
              <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Assistente Idea 360</p>
              <p className="text-white/45 text-xs">Risposte in tempo reale · un consulente ti risponde entro 2 ore</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 no-scrollbar" data-testid="chat-messages">
            {messages.map((m, i) => <Bubble key={i} role={m.role} text={m.content} />)}
            {showForm && !leadDone && (
              <ChatLeadForm
                sessionId={sessionId.current}
                onDone={(reply, name) => {
                  setShowForm(false);
                  setLeadDone(true);
                  setLeadName(name);
                  setMessages((m) => [...m, { role: "assistant", content: reply }]);
                }}
              />
            )}
            {leadDone && (
              <div className="text-center pt-2 space-y-3" data-testid="chat-lead-confirm">
                <p className="text-xs text-[#FACC15]">Richiesta inviata al team. Ti rispondiamo entro 2 ore.</p>
                <a
                  href={whatsappLink(
                    `Ciao, sono ${leadName || "un visitatore del sito"}: ho lasciato i miei recapiti nella chat del sito Idea 360 e vorrei parlare con un consulente.`
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-black text-sm font-semibold px-4 py-2.5 hover:bg-[#1ebe5b] transition-colors"
                  data-testid="chat-whatsapp-button"
                >
                  <MessageCircle size={16} />
                  Scrivici subito su WhatsApp
                </a>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="p-3 border-t border-[#1f1f1f] flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Scrivi la tua domanda…"
              className="field-input !py-3 text-sm"
              maxLength={1500}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              aria-label="Invia"
              data-testid="chat-send"
              className="btn-yellow !px-5 !py-0 !min-h-[48px] flex items-center justify-center shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Send size={18} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
